import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { Icon } from "@iconify/react";

export default function AddVenueCity() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);



    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            newCity.setFieldValue("image", file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const newCity = useFormik({
        initialValues: {
            name: '',
            image: null,
            location: '',
            description: '',
            lat: '',
            lng: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            description: Yup.string().required('Required'),
            location: Yup.string()
                .matches(
                    /^https:\/\/www\.google\.com\/maps\/embed\?pb=.*/i,
                    'Please enter a valid embedded Google Maps URL'
                )
                .required('Location URL is required'),
            lat: Yup.number()
                .typeError('Latitude must be a decimal number')
                .required('Latitude is required')
                .min(-90, 'Latitude must be at least -90')
                .max(90, 'Latitude must be at most 90'),
            lng: Yup.number()
                .typeError('Longitude must be a decimal number')
                .required('Longitude is required')
                .min(-180, 'Longitude must be at least -180')
                .max(180, 'Longitude must be at most 180'),
            image: Yup.mixed()
                .required('Image is required')
                .test('fileSize', "File size must be less than 1MB", (value) => {
                    return value && value.size <= 1 * 1024 * 1024; // 1MB
                })
                .test('fileFormat', 'Unsupported Format', (value) => {
                    return value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type);
                }),
        }),
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('location', values.location);
                formData.append("coordinate", JSON.stringify({
                    lat: parseFloat(values.lat),
                    lng: parseFloat(values.lng)
                }));
                formData.append('description', values.description);
                formData.append('image', values.image);

                const response = await axios.post(`${apiUrl}event-service/city`, formData, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'multipart/form-data',
                        'accept': 'application/json',
                    },
                });

                if (response.data?.success) {
                    resetForm();
                    setTimeout(() => navigate('/venue-cities'), 500);
                    toast.success(response.data?.message || 'Venue city successfully created!');
                } else {
                    throw new Error('Failed to create venue city');
                }
            } catch (error) {
                console.error('Error:', error.response?.data?.message || error.message);
                toast.error(error.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <TailwindLayout>
            <TopBar />
            <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">

                    <form onSubmit={newCity.handleSubmit} className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Venue City</h2>
                        <div className="border-t pt-6">
                            <div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="name">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="eg. Jaffna"
                                        value={newCity.values.name}
                                        onChange={newCity.handleChange}
                                        onBlur={newCity.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {newCity.touched.name && newCity.errors.name && <div className="text-red-500 text-sm">{newCity.errors.name}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="location">
                                        Location <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        placeholder="eg. https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.1234567890123!2d-122.4194156846814!3d37.7749292797599!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808c8c8c8c8c%3A0x8c8c8c8c8c8c8c8c%2C+San+Francisco%2C+CA+94103&output=embed"
                                        value={newCity.values.location}
                                        onChange={newCity.handleChange}
                                        onBlur={newCity.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {newCity.touched.location && newCity.errors.location && <div className="text-red-500 text-sm">{newCity.errors.location}</div>}
                                </div>
                                <div className='mb-4'>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Latitude <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lat"
                                                placeholder="eg. 37.7749"
                                                value={newCity.values.lat}
                                                onChange={newCity.handleChange}
                                                onBlur={newCity.handleBlur}
                                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                            />
                                            {newCity.touched.lat && newCity.errors.lat && <div className="text-red-500 text-sm">{newCity.errors.lat}</div>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Longitude <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lng"
                                                placeholder="eg. 122.4194"
                                                value={newCity.values.lng}
                                                onChange={newCity.handleChange}
                                                onBlur={newCity.handleBlur}
                                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                            />
                                            {newCity.touched.lng && newCity.errors.lng && <div className="text-red-500 text-sm">{newCity.errors.lng}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="address">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="description"
                                        placeholder="eg. anything about the city"
                                        value={newCity.values.description}
                                        onChange={newCity.handleChange}
                                        onBlur={newCity.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {newCity.touched.description && newCity.errors.description && <div className="text-red-500 text-sm">{newCity.errors.description}</div>}
                                </div>
                                <div className='mb-4'>
                                    <div className="text-sm font-medium mb-2">Image <span className="text-gray-500">(Recommended size: below 1mb, 1:1, JPG/JPEG/PNG format)</span></div>
                                    <div className="flex items-center space-x-4">
                                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center relative">
                                            {previewImage ? (
                                                <>
                                                    <img
                                                        src={previewImage}
                                                        alt="City image"
                                                        className="w-full h-full object-cover rounded-md cursor-pointer"
                                                        onClick={() => setIsPreviewOpen(true)}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPreviewImage(null);
                                                            newCity.setFieldValue('image', null);
                                                            newCity.setFieldTouched('image', true);
                                                        }}
                                                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-colors"
                                                        aria-label="Remove image"
                                                    >
                                                        <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                                    </button>
                                                </>
                                            ) : (
                                                <Icon icon="iconoir:add-media-image" className="text-xl" />
                                            )}
                                        </div>
                                        <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                                            <span>{previewImage ? 'Change Image' : 'Upload Image'}</span>
                                            <input type="file" className="hidden" onChange={handleImageChange} />
                                        </label>
                                    </div>
                                    {newCity.touched.image && newCity.errors.image && (
                                        <p className="text-red-500 text-sm mt-1">{newCity.errors.image}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                            {isLoading && (
                                <span className="ml-2 text-gray-600">Saving....</span>
                            )}
                            {isLoading && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                    <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                                        <svg
                                            className="animate-spin h-8 w-8 text-blue-600 mb-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            ></path>
                                        </svg>

                                        <p className="text-sm font-medium text-gray-700">Saving...</p>
                                    </div>
                                </div>
                            )}

                        </div>
                        {isPreviewOpen && (
                            <div
                                className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                                onClick={() => setIsPreviewOpen(false)}
                            >
                                <div className="relative">
                                    <img
                                        src={previewImage}
                                        alt="Big Preview"
                                        className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
                                    />

                                    <button
                                        className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-md"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsPreviewOpen(false);
                                        }}
                                    >
                                        <Icon icon="carbon:close-outline" className="text-black w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                        )}

                    </form>
                </div>
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    )
}